'use client';

import { forwardRef } from 'react';
import { MultiSelect as MantineMultiSelect } from '@mantine/core';
import type { MultiSelectProps as MantineMultiSelectProps } from '@mantine/core';

import { SELECT_TRANSITION_PROPS } from '../select/constants';
import type { LockedProps } from '../types';

export type MultiSelectProps = Omit<MantineMultiSelectProps, LockedProps>;

/**
 * ### MultiSelect
 *
 * @description
 * Mantine's MultiSelect minus per-call-site style overrides (see {@link LockedProps}).
 * Dropdown transitions are applied by default and can be overridden via `comboboxProps`.
 * For the v6-compat `creatable` flow use `CreatableMultiSelect` in
 * `@highlight-cp/core-ui-compositions`.
 *
 * @example
 * <MultiSelect data={[{ value: 'a', label: 'A' }]} value={value} onChange={setValue} />
 *
 * @param props - {@link MultiSelectProps}
 * @docs {@link https://mantine.dev/core/multi-select/}
 */
export const MultiSelect = forwardRef<HTMLInputElement, MultiSelectProps>(
  ({ comboboxProps, value, placeholder, ...rest }, ref) => (
    <MantineMultiSelect
      ref={ref}
      {...rest}
      value={value}
      // v7 always renders the placeholder alongside selected pills; suppress it once a value exists.
      placeholder={value?.length ? undefined : placeholder}
      inputWrapperOrder={['label', 'input', 'error', 'description']}
      comboboxProps={{ transitionProps: SELECT_TRANSITION_PROPS, ...comboboxProps }}
    />
  ),
);

MultiSelect.displayName = 'MultiSelect';
